import { createSelector } from 'reselect';

import { userInfoSelectors } from 'wanderu-ducks-account';
import { carrierDataSelectors } from 'wanderu-ducks-carriers';
import {
    liveCheckSelectors,
    liveCheckCachedTripAction,
} from 'wanderu-ducks-live-check';
import { tripSelectors } from 'wanderu-ducks-trips';
import {
    selectCurrency,
    selectLocale,
} from 'wanderu-i18n';

import {
    submitFormAction,
    fetchTripBookingInfoAction,
    fetchFormAndSummaryAction,
} from '..';
import {
    SLIMMED_PROPERTIES,
    TRIP_LEG_ITEMS_FROM_QUOTE_DATA,
    USER_ACCOUNT_FIELD_IDS,
    USER_INFO_FIELD_NAME_MAP,
    READ_ONLY_FIELDS,
    ACCOUNT_PASSWORD_ID,
    INSURANCE_SELECTION_ID,
    MARKETING_OPT_IN_FIELD_ID,
    USER_EMAIL_FIELD_ID,
    LIVE_CHECK_CACHE_EXPIRED_ERROR,
    US_NON_STATE_TERRITORIES,
} from '../../constants';
import {
    getFormField,
    getPriceListForTrip,
    getPriceListForTripForEvents,
    checkTripNotFound,
    setAllFormFieldsAsModified,
} from '../../utils';
import SLICE_NAMESPACE from '../types';
import NAMESPACE from './types';
import { mapPollingStatus } from './utils';

function enrichFormWithCountries(form, countries) {
    const enrichedFields = form.fields.map((field) => {
        let enrichedField = { ...field };

        if (enrichedField.type === 'country' && enrichedField.values.length === 0) {
            enrichedField.values = countries;
        }

        if (!!enrichedField.fields && enrichedField.fields.length) {
            enrichedField = enrichFormWithCountries(enrichedField, countries);
        }

        return enrichedField;
    });

    return {
        ...form,
        fields: enrichedFields,
    };
}

function enrichFormWithStates(form, formCountry, countries) {
    const enrichedFields = form.fields.map((field) => {
        let enrichedField = { ...field };

        if (enrichedField.type === 'state') {
            const localityCountry = countries.find(country => country.id === formCountry);
            enrichedField.values = localityCountry?.states || [];
        }

        if (!!enrichedField.fields && enrichedField.fields.length) {
            enrichedField = enrichFormWithStates(enrichedField, formCountry, countries);
        }

        return enrichedField;
    });

    return {
        ...form,
        fields: enrichedFields,
    };
}

function setFieldValidation(field, validation) {
    if (!validation || validation.isValid === undefined) {
        return field;
    }

    return {
        ...field,
        errorMap: validation.errorMap,
        errorMessage: validation.errorMessage,
        isValid: validation.isValid,
    };
}

function enrichFormWithValidations(form, validations) {
    const enrichedFields = form.fields.map((field) => {
        const validation = getFormField(validations, field.id);

        let enrichedField = setFieldValidation(field, validation);

        if (enrichedField?.fields?.length && validation) {
            enrichedField = enrichFormWithValidations(enrichedField, validation);
        }

        return enrichedField;
    });

    return {
        ...form,
        fields: enrichedFields,
    };
}

function formatForm(formData, formCountry, localities, validations) {
    if (!formData) {
        return undefined;
    }

    let form = formData;

    if (localities.countries) {
        form = enrichFormWithCountries(form, localities.countries);

        if (formCountry) {
            form = enrichFormWithStates(form, formCountry, localities.countries);
        }
    }

    if (validations) {
        form = enrichFormWithValidations(form, validations);
    }

    return form;
}

function formatFormWithModifications(formData) {
    if (!formData) {
        return undefined;
    }

    return setAllFormFieldsAsModified(formData);
}

function selectData(state) {
    return state[SLICE_NAMESPACE][NAMESPACE];
}

function selectFormData(state) {
    return selectData(state).form;
}

function selectValidations(state) {
    return selectData(state).validations;
}

function selectDetails(state) {
    return selectData(state).details;
}

function selectPayment(state) {
    return selectData(state).payment;
}

function selectPaymentMethods(state) {
    return selectData(state).paymentMethods;
}

function selectLocalities(state) {
    return selectData(state).localities;
}

function compressForm(form) {
    return Object.keys(form).reduce((obj, key) => {
        if (key === 'fields') {
            return {
                ...obj,
                fields: form.fields.map(compressForm),
            };
        }

        if (SLIMMED_PROPERTIES.includes(key)) {
            return {
                ...obj,
                [key]: form[key],
            };
        }

        return obj;
    }, {});
}

function selectSlimFormData(state) {
    const form = selectFormData(state);

    if (!form) {
        return undefined;
    }

    return compressForm(form);
}

function selectFieldValue(state, path) {
    const form = selectFormData(state);

    if (!form || !getFormField(form, path)) {
        return undefined;
    }

    return getFormField(form, path).value;
}

function selectUserFormInfo(state) {
    return Object.entries(USER_ACCOUNT_FIELD_IDS).reduce((obj, [key, path]) => {
        const data = selectFieldValue(state, path);
        return data
            ? { ...obj, [key]: data }
            : obj;
    }, {});
}

function selectSeatInfo(state) {
    const {
        seats,
        wheelchairs,
    } = selectData(state);

    return { seats, wheelchairs };
}

function selectTripLiveCheck(state, tripId) {
    const {
        seats,
        wheelchairs,
        selectedFaresMap = {},
    } = selectBookingInfo(state);

    if (!tripId) {
        return undefined;
    }

    const lcParams = {
        seats,
        selectedFare: selectedFaresMap[tripId],
        tripId,
        wheelchairs,
    };
    return liveCheckSelectors.selectTripLiveCheck(state, lcParams);
}

function selectTripLiveChecks(state) {
    const { tripIds = [] } = selectBookingInfo(state);

    if (!tripIds.length) {
        return undefined;
    }

    return tripIds.map((tripId) => selectTripLiveCheck(state, tripId));
}

function selectBookingInfo(state) {
    return selectData(state);
}

function selectSeatingInfo(state) {
    const bookingInfo = selectBookingInfo(state) || {};

    return {
        seats: bookingInfo.seats,
        wheelChairs: bookingInfo.wheelchairs,
    };
}

const selectForm = createSelector(
    selectFormData,
    (state) => selectFieldValue(state, USER_ACCOUNT_FIELD_IDS.country),
    selectLocalities,
    selectValidations,
    formatForm,
);

const selectFormWithModifications = createSelector(
    selectFormData,
    formatFormWithModifications,
);

function selectQuoteSummary(state) {
    return selectData(state).quoteSummary;
}

function selectOrderIdMap(state) {
    return selectData(state).orderIds;
}

function selectTravelInsurance(state) {
    const quoteSummary = selectQuoteSummary(state);

    if (!quoteSummary?.travelInsurance) {
        return undefined;
    }

    return quoteSummary.travelInsurance.map((product) => ({
        ...product,
        pricing: Object.keys(product.pricing).reduce((priceObj, priceId) => ({
            ...priceObj,
            [priceId]: Number(product.pricing[priceId]),
        }), {}),
    }));
}

function selectOrderId(state, tripId) {
    return selectOrderIdMap(state)[tripId];
}

function selectBookingStatusData(state, orderId) {
    return selectData(state).bookingStatus[orderId];
}

function selectBookingStatus(state, orderId) {
    const data = selectBookingStatusData(state, orderId);
    if (!data) {
        return undefined;
    }
    return mapPollingStatus(data);
}

function selectBookingStatuses(state) {
    const orderIdMap = selectOrderIdMap(state);

    return Object.entries(orderIdMap).reduce((statuses, [tripId, orderId]) => {
        if (!selectBookingStatus(state, orderId)) {
            return statuses;
        }

        return {
            ...statuses,
            [tripId]: selectBookingStatus(state, orderId),
        };
    }, {});
}

function selectCarrierData(state) {
    const liveChecks = selectTripLiveChecks(state) || [];

    if (!liveChecks.filter(Boolean).length) {
        return {};
    }

    return liveChecks.reduce((obj, lc) => ({
        ...obj,
        [lc?.tripData?.trip_id]:
            carrierDataSelectors.selectCarrierData(state, lc?.tripData?.carrier),
    }), {});
}

function selectInsuranceOrderId(state) {
    return selectData(state).insuranceOrderId;
}

function selectInsuranceStatusData(state) {
    return selectData(state).insuranceStatus;
}

function selectInsuranceStatus(state) {
    const data = selectInsuranceStatusData(state);
    if (!data) {
        return undefined;
    }
    return mapPollingStatus(data);
}

function selectTripConfirmationMap(state) {
    return selectData(state).tripConfirmations;
}

function selectTripConfirmations(state) {
    const tripConfirmationMap = selectTripConfirmationMap(state);

    return Object.entries(tripConfirmationMap).map(([tripId, confirmation]) => ([
        tripId,
        confirmation,
    ]));
}

function getTripLegItemFromQuoteSummary({
    itemId, state, tripLeg, returnTrip = false,
}) {
    const quoteData = returnTrip
        ? selectReturnQuoteData(state)
        : selectDepartQuoteData(state);

    if (!quoteData || !quoteData[tripLeg.trip_id]) {
        return [];
    }

    return quoteData[tripLeg.trip_id][itemId];
}

function setTripLegData({ tripLeg, state, returnTrip }) {
    const leg = { ...tripLeg };
    const tripInformation = tripSelectors.selectTripInformation(state, leg.trip_id);
    Object.entries(TRIP_LEG_ITEMS_FROM_QUOTE_DATA)
        .forEach(([itemKey, itemId]) => {
            leg[itemKey] = getTripLegItemFromQuoteSummary({
                itemId,
                returnTrip,
                state,
                tripLeg: leg,
            });
        });

    if (tripInformation?.fareTitle) {
        leg.fareTitle = tripInformation.fareTitle;
    }

    return leg;
}

function selectDepartLegs(state) {
    const { departLegIds = [] } = selectBookingInfo(state);
    const departLegs = departLegIds.map(
        (tripId) => selectTripLiveCheck(state, tripId)?.tripData || [],
    );

    return departLegs.map((tripLeg) => setTripLegData({ state, tripLeg }));
}

function selectReturnLegs(state) {
    const { returnLegIds } = selectBookingInfo(state);
    if (!returnLegIds) {
        return undefined;
    }

    const returnLegs = returnLegIds.map(
        (tripId) => selectTripLiveCheck(state, tripId)?.tripData || [],
    );

    return returnLegs.map((tripLeg) => setTripLegData({ returnTrip: true, state, tripLeg }));
}

function mapQuoteData(quoteData, tripIds) {
    return quoteData?.trips.reduce((obj, data) => {
        const { id } = data;
        if (!tripIds.includes(id)) {
            return obj;
        }

        return { ...obj, [id]: data };
    }, {});
}

function selectDepartQuoteData(state) {
    const { departLegIds } = selectBookingInfo(state);
    const quoteData = selectQuoteSummary(state);

    return mapQuoteData(quoteData, departLegIds);
}

function selectReturnQuoteData(state) {
    const { returnLegIds } = selectBookingInfo(state);
    if (!returnLegIds) {
        return undefined;
    }

    const quoteData = selectQuoteSummary(state);

    return mapQuoteData(quoteData, returnLegIds);
}

function selectInsuranceSelected(state) {
    return selectData(state).insuranceSelected;
}

function selectUserInfo(state) {
    const isLoggedIn = userInfoSelectors.selectUserLoggedIn(state);

    if (!isLoggedIn) {
        return undefined;
    }

    const userInfo = userInfoSelectors.selectUserInfo(state);
    const flattenedUserInfo = {
        ...userInfo,
        ...userInfo?.billingAddress,
    };

    const formPathInfo = Object.keys(USER_ACCOUNT_FIELD_IDS).reduce((obj, name) => {
        const value = USER_ACCOUNT_FIELD_IDS[name];

        const key = USER_INFO_FIELD_NAME_MAP[name];

        return {
            ...obj,
            [value]: flattenedUserInfo[key],
        };
    }, {});

    return {
        ...formPathInfo,
    };
}

function selectFieldsByValidity({ fields, key = 'id', valid = true }) {
    return fields.reduce((validMap, field) => {
        if (field.fields) {
            return [
                ...validMap,
                ...selectFieldsByValidity({ fields: field.fields, key, valid }),
            ];
        }

        if (field.isValid !== undefined && field.isValid === valid) {
            return [
                ...validMap,
                field[key],
            ];
        }

        return validMap;
    }, []);
}

function selectValidFields(state) {
    const form = selectForm(state);

    if (!form) {
        return undefined;
    }

    return selectFieldsByValidity({ fields: form.fields });
}

function selectInvalidFields(state) {
    const form = selectForm(state);

    if (!form) {
        return undefined;
    }

    return selectFieldsByValidity({ fields: form.fields, valid: false });
}

const selectInvalidLabels = createSelector(
    selectForm,
    (form) => {
        if (!form) {
            return undefined;
        }

        return selectFieldsByValidity({ fields: form.fields, key: 'label', valid: false });
    }
);

function isReadOnlyField(field) {
    return !!READ_ONLY_FIELDS.includes(field.type);
}

function fieldIsValid(field, insuranceVisible) {
    const isHiddenInsuranceField = field.id === INSURANCE_SELECTION_ID && !insuranceVisible;

    return isHiddenInsuranceField
        || (field.isValid !== undefined && !!field.isValid)
        || isReadOnlyField(field);
}

function selectFieldsAreValid(fields, insuranceVisible) {
    return fields.every((field) => {
        if (field.fields) {
            return selectFieldsAreValid(field.fields, insuranceVisible);
        }

        return fieldIsValid(field, insuranceVisible);
    });
}

function selectAllFieldsAreValid(state) {
    const form = selectForm(state);
    const insuranceVisible = selectInsuranceVisible(state);

    if (!form) {
        return undefined;
    }

    return selectFieldsAreValid(form.fields, insuranceVisible);
}

function selectAllPurchasesSuccess(state) {
    const bookingSuccess = submitFormAction.selectSuccess(state);
    const insuranceSelected = selectInsuranceSelected(state);
    const insuranceStatus = selectInsuranceStatus(state);

    let allPurchasesSuccessList = [bookingSuccess];

    if (insuranceSelected) {
        allPurchasesSuccessList.push(insuranceStatus?.status === 'COMPLETED');
    }

    return allPurchasesSuccessList.every((status) => status === true);
}

const selectPriceList = createSelector(
    selectForm,
    selectDepartLegs,
    selectReturnLegs,
    selectInsuranceSelected,
    selectTravelInsurance,
    (form, departLegs, returnLegs, isInsuranceSelected, travelInsurance) => {
        if (!form) {
            return [];
        }
        const departTrip = {
            legs: departLegs,
        };
        const returnTrip = {
            legs: returnLegs,
        };
        let priceList = getPriceListForTrip({ departTrip, returnTrip });

        if (isInsuranceSelected) {
            priceList = [
                ...priceList,
                ...travelInsurance.map((product) => product?.pricing),
            ];
        }

        return priceList;
    },
);

const selectPriceListForEvents = createSelector(
    selectForm,
    selectDepartLegs,
    selectReturnLegs,
    selectInsuranceSelected,
    selectTravelInsurance,
    (form, departLegs, returnLegs, isInsuranceSelected, travelInsurance) => {
        if (!form) {
            return [];
        }
        const departTrip = {
            legs: departLegs,
        };
        const returnTrip = {
            legs: returnLegs,
        };
        let priceList = getPriceListForTripForEvents({ departTrip, returnTrip });

        if (isInsuranceSelected) {
            priceList = [
                ...priceList,
                {
                    insurance: travelInsurance.map((product) => product?.pricing),
                },
            ];
        }

        return priceList;
    },
);

function selectUserAccountExists(state) {
    return selectData(state).userAccountExists;
}

function selectUserEmail(state) {
    return selectData(state).userEmail;
}

function selectUserEmailValidHelper(fields) {
    return fields.reduce((emailField, field) => {
        if (Object.keys(emailField).length) {
            return emailField;
        }

        if (field.fields) {
            return selectUserEmailValidHelper(field.fields);
        }

        if (field.id === USER_EMAIL_FIELD_ID) {
            return field;
        }

        return emailField;
    }, {});
}

function selectUserEmailValid(state) {
    const form = selectForm(state);

    if (!form) {
        return undefined;
    }

    return !!selectUserEmailValidHelper(form.fields).isValid;
}

function selectPasswordFieldHelper(form) {
    return form.fields.reduce((passwordField, field) => {
        if (Object.keys(passwordField).length) {
            return passwordField;
        }

        if (field.fields) {
            return selectPasswordFieldHelper(field);
        }

        if (field.id === ACCOUNT_PASSWORD_ID) {
            return field;
        }

        return passwordField;
    }, {});
}

function selectPasswordField(state) {
    return selectPasswordFieldHelper(selectForm(state));
}

function selectInsuranceVisible(state) {
    const insuranceSupportMap = {
        CAD: ['CA', 'Canada'],
        USD: ['US', 'United States'],
    };

    const currency = selectCurrency(state);
    let billingCountry = selectFieldValue(state, USER_ACCOUNT_FIELD_IDS.country);
    const stateProvince = selectFieldValue(state, USER_ACCOUNT_FIELD_IDS.stateProvince);

    if (!billingCountry) {
        const locale = selectLocale(state);
        const [, location] = locale.split('-');

        billingCountry = location.toUpperCase();
    }

    return !!insuranceSupportMap[currency]?.includes(billingCountry)
        && !US_NON_STATE_TERRITORIES.includes(stateProvince);
}

function selectItineraryId(state) {
    return selectData(state).itineraryId;
}

function selectMarketingOptInHelper(form) {
    return form.fields.reduce((marketingField, field) => {
        if (Object.keys(marketingField).length) {
            return marketingField;
        }

        if (field.fields) {
            return selectMarketingOptInHelper(field);
        }

        if (field.id === MARKETING_OPT_IN_FIELD_ID) {
            return field;
        }

        return marketingField;
    }, {});
}

function selectMarketingOptIn(state) {
    return !!selectMarketingOptInHelper(selectForm(state)).value;
}

function selectEarliestExpiry(state, tokenMap) {
    return Object.entries(tokenMap)
        .reduce((earliestExpiresAt, [tripId, searchToken]) => {
            const expiresAt = liveCheckCachedTripAction.selectExpiresAt(
                state,
                `${tripId}-${searchToken}`,
            );

            if (typeof earliestExpiresAt === 'undefined' || expiresAt < earliestExpiresAt) {
                return expiresAt;
            }

            return earliestExpiresAt;
        }, undefined);
}

const selectLivecheckTripNotFound = createSelector(
    (state) => fetchTripBookingInfoAction.selectError(state),
    (error) => checkTripNotFound(error),
);

const selectFormFetchTripNotFound = createSelector(
    (state) => fetchFormAndSummaryAction.selectError(state),
    (error) => checkTripNotFound(error),
);

const selectLivecheckExpired = createSelector(
    (state) => fetchTripBookingInfoAction.selectError(state),
    (error) => error?.code === LIVE_CHECK_CACHE_EXPIRED_ERROR,
);

function selectGoogleDepartId(state) {
    return selectData(state).googlePlaceIds?.depart;
}

function selectGoogleDestinationId(state) {
    return selectData(state).googlePlaceIds?.destination;
}

export {
    selectAllFieldsAreValid,
    selectAllPurchasesSuccess,
    selectBookingInfo,
    selectBookingStatus,
    selectBookingStatuses,
    selectCarrierData,
    selectDepartLegs,
    selectDepartQuoteData,
    selectDetails,
    selectEarliestExpiry,
    selectFieldValue,
    selectFieldsAreValid,
    selectFieldsByValidity,
    selectForm,
    selectFormData,
    selectFormFetchTripNotFound,
    selectFormWithModifications,
    selectGoogleDepartId,
    selectGoogleDestinationId,
    selectInsuranceOrderId,
    selectInsuranceSelected,
    selectInsuranceStatus,
    selectInsuranceVisible,
    selectInvalidFields,
    selectInvalidLabels,
    selectItineraryId,
    selectLivecheckExpired,
    selectLivecheckTripNotFound,
    selectMarketingOptIn,
    selectOrderId,
    selectPasswordField,
    selectPayment,
    selectPaymentMethods,
    selectPriceList,
    selectPriceListForEvents,
    selectQuoteSummary,
    selectReturnLegs,
    selectReturnQuoteData,
    selectSeatInfo,
    selectSeatingInfo,
    selectSlimFormData,
    selectTravelInsurance,
    selectTripConfirmationMap,
    selectTripConfirmations,
    selectTripLiveCheck,
    selectTripLiveChecks,
    selectUserAccountExists,
    selectUserEmail,
    selectUserEmailValid,
    selectUserFormInfo,
    selectUserInfo,
    selectValidFields,
};
